import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth-store';
import { useSettingsStore, type DateRangePreset } from '@/stores/settings-store';
import { api } from '@/lib/api';

// Convert date range preset to GA4 relative date strings
function getGA4DateRange(preset: DateRangePreset): { current: { startDate: string; endDate: string }; previous: { startDate: string; endDate: string } } {
  const days = parseInt(preset.replace('d', ''), 10);
  return {
    current: { startDate: `${days}daysAgo`, endDate: 'yesterday' },
    previous: { startDate: `${days * 2}daysAgo`, endDate: `${days + 1}daysAgo` },
  };
}

interface KPIData {
  sessions: number;
  users: number;
  pageviews: number;
  avgSessionDuration: number; // in seconds
  bounceRate: number; // percentage
  sessionsChange: number;
  usersChange: number;
  pageviewsChange: number;
  avgSessionDurationChange: number;
  bounceRateChange: number;
}

/**
 * Fetch KPI data for the dashboard
 */
export function useGA4KPIs() {
  const { activePropertyId, properties } = useAuthStore();
  const { dateRange } = useSettingsStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const ga4PropertyId = activeProperty?.ga4Property?.id?.replace('properties/', '') || activePropertyId;
  const { current, previous } = getGA4DateRange(dateRange);

  return useQuery({
    queryKey: ['ga4-kpis', ga4PropertyId, dateRange],
    queryFn: async (): Promise<KPIData | null> => {
      if (!ga4PropertyId || !activeProperty?.ga4Property) {
        return null;
      }

      // Fetch current period
      const currentPeriodRequest = {
        propertyId: ga4PropertyId,
        dateRange: current,
        metrics: ['sessions', 'totalUsers', 'screenPageViews', 'averageSessionDuration', 'bounceRate'],
      };

      // Fetch previous period for comparison
      const previousPeriodRequest = {
        propertyId: ga4PropertyId,
        dateRange: previous,
        metrics: ['sessions', 'totalUsers', 'screenPageViews', 'averageSessionDuration', 'bounceRate'],
      };

      try {
        const [currentData, previousData] = await Promise.all([
          api.google.runGAReport(currentPeriodRequest),
          api.google.runGAReport(previousPeriodRequest),
        ]);

        if (!currentData?.rows?.[0]) {
          return null;
        }

        const currentMetrics = currentData.rows[0].metricValues || [];
        const previousMetrics = previousData?.rows?.[0]?.metricValues || [];

        const getValue = (metrics: Array<{ value: string }>, index: number): number => {
          const val = parseFloat(metrics[index]?.value || '0');
          return isNaN(val) ? 0 : val;
        };

        const calcChange = (current: number, previous: number): number => {
          if (previous === 0) return current > 0 ? 100 : 0;
          return ((current - previous) / previous) * 100;
        };

        const sessions = getValue(currentMetrics, 0);
        const users = getValue(currentMetrics, 1);
        const pageviews = getValue(currentMetrics, 2);
        const avgSessionDuration = getValue(currentMetrics, 3);
        const bounceRate = getValue(currentMetrics, 4) * 100; // Convert to percentage

        const prevSessions = getValue(previousMetrics, 0);
        const prevUsers = getValue(previousMetrics, 1);
        const prevPageviews = getValue(previousMetrics, 2);
        const prevAvgSessionDuration = getValue(previousMetrics, 3);
        const prevBounceRate = getValue(previousMetrics, 4) * 100;

        return {
          sessions,
          users,
          pageviews,
          avgSessionDuration,
          bounceRate,
          sessionsChange: calcChange(sessions, prevSessions),
          usersChange: calcChange(users, prevUsers),
          pageviewsChange: calcChange(pageviews, prevPageviews),
          avgSessionDurationChange: calcChange(avgSessionDuration, prevAvgSessionDuration),
          bounceRateChange: calcChange(bounceRate, prevBounceRate),
        };
      } catch (error) {
        console.error('Failed to fetch GA4 KPIs:', error);
        throw error;
      }
    },
    enabled: !!ga4PropertyId && !!activeProperty?.ga4Property,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Fetch traffic over time for charts
 */
export function useGA4TrafficOverTime() {
  const { activePropertyId, properties } = useAuthStore();
  const { dateRange } = useSettingsStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const ga4PropertyId = activeProperty?.ga4Property?.id?.replace('properties/', '') || activePropertyId;
  const { current } = getGA4DateRange(dateRange);

  return useQuery({
    queryKey: ['ga4-traffic-over-time', ga4PropertyId, dateRange],
    queryFn: async () => {
      if (!ga4PropertyId || !activeProperty?.ga4Property) {
        return null;
      }

      const request = {
        propertyId: ga4PropertyId,
        dateRange: current,
        dimensions: ['date'],
        metrics: ['sessions', 'totalUsers', 'screenPageViews'],
        orderBy: { dimension: 'date' },
      };

      try {
        const response = await api.google.runGAReport(request);

        if (!response?.rows) {
          return [];
        }

        return response.rows.map((row: any) => ({
          date: row.dimensionValues?.[0]?.value || '',
          sessions: parseInt(row.metricValues?.[0]?.value || '0', 10),
          users: parseInt(row.metricValues?.[1]?.value || '0', 10),
          pageviews: parseInt(row.metricValues?.[2]?.value || '0', 10),
        }));
      } catch (error) {
        console.error('Failed to fetch GA4 traffic over time:', error);
        throw error;
      }
    },
    enabled: !!ga4PropertyId && !!activeProperty?.ga4Property,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Fetch top pages
 */
export function useGA4TopPages(limit = 10) {
  const { activePropertyId, properties } = useAuthStore();
  const { dateRange } = useSettingsStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const ga4PropertyId = activeProperty?.ga4Property?.id?.replace('properties/', '') || activePropertyId;
  const { current } = getGA4DateRange(dateRange);

  return useQuery({
    queryKey: ['ga4-top-pages', ga4PropertyId, limit, dateRange],
    queryFn: async () => {
      if (!ga4PropertyId || !activeProperty?.ga4Property) {
        return null;
      }

      const request = {
        propertyId: ga4PropertyId,
        dateRange: current,
        dimensions: ['pagePath'],
        metrics: ['screenPageViews', 'averageSessionDuration', 'bounceRate'],
        orderBy: { metric: 'screenPageViews', desc: true },
        limit,
      };

      try {
        const response = await api.google.runGAReport(request);

        if (!response?.rows) {
          return [];
        }

        return response.rows.map((row: any) => ({
          pagePath: row.dimensionValues?.[0]?.value || '',
          pageviews: parseInt(row.metricValues?.[0]?.value || '0', 10),
          avgDuration: parseFloat(row.metricValues?.[1]?.value || '0'),
          bounceRate: parseFloat(row.metricValues?.[2]?.value || '0') * 100,
        }));
      } catch (error) {
        console.error('Failed to fetch GA4 top pages:', error);
        throw error;
      }
    },
    enabled: !!ga4PropertyId && !!activeProperty?.ga4Property,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Fetch traffic sources
 */
export function useGA4TrafficSources(limit = 10) {
  const { activePropertyId, properties } = useAuthStore();
  const { dateRange } = useSettingsStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const ga4PropertyId = activeProperty?.ga4Property?.id?.replace('properties/', '') || activePropertyId;
  const { current } = getGA4DateRange(dateRange);

  return useQuery({
    queryKey: ['ga4-traffic-sources', ga4PropertyId, limit, dateRange],
    queryFn: async () => {
      if (!ga4PropertyId || !activeProperty?.ga4Property) {
        return null;
      }

      const request = {
        propertyId: ga4PropertyId,
        dateRange: current,
        dimensions: ['sessionSource', 'sessionMedium'],
        metrics: ['sessions', 'totalUsers'],
        orderBy: { metric: 'sessions', desc: true },
        limit,
      };

      try {
        const response = await api.google.runGAReport(request);

        if (!response?.rows) {
          return [];
        }

        return response.rows.map((row: any) => ({
          source: row.dimensionValues?.[0]?.value || '(direct)',
          medium: row.dimensionValues?.[1]?.value || '(none)',
          sessions: parseInt(row.metricValues?.[0]?.value || '0', 10),
          users: parseInt(row.metricValues?.[1]?.value || '0', 10),
        }));
      } catch (error) {
        console.error('Failed to fetch GA4 traffic sources:', error);
        throw error;
      }
    },
    enabled: !!ga4PropertyId && !!activeProperty?.ga4Property,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
